import { useState, useEffect } from "react";
import ChatMedia from "./ChatMedia";
import FileList from "./FileList";
import ChatTime from "./ChatTime";
import ChatActionsDropdown from "./ChatActionsDropdown";
import ReactionPicker from "./ReactionPicker";
import _ from "lodash";

const ChatContent = ({
  chat,
  chatMessages,
  key,
  activeTab,
  currentUser,
  formatDate,
  handleForwardCLick,
  deleteMessage,
  isGroup = false,
  editHandler = () => { },
  updateReactions = () => { },
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [reactions, setReactions] = useState(chat.reactions || []);
  const [hoverTimeout, setHoverTimeout] = useState(null);

  // ✅ Sync reactions when chat updates
  useEffect(() => {
    setReactions(chat.reactions || []);
  }, [chat.reactions]);

  const isSender = chat.sender === currentUser._id;
  const isImage = chat?.media?.[0]?.fileType?.startsWith("image");
  const isAudio = chat?.media?.[0]?.fileType === "audio/webm";
  const isOtherFile = chat?.media?.[0] && !isImage && !isAudio;

  // ✅ Smooth hover handlers (no flicker)
  const handleMouseEnter = () => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    setShowPicker(true);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setShowPicker(false);
    }, 150);
    setHoverTimeout(timeout);
  };

  // ✅ Reaction Logic
  const handleReaction = async (emoji) => {
    let updatedReactions = [...reactions];

    const existingIndex = updatedReactions.findIndex(
      (r) => r.user === currentUser._id
    );

    if (existingIndex !== -1) {
      if (updatedReactions[existingIndex].emoji === emoji) {
        updatedReactions.splice(existingIndex, 1); // remove
      } else {
        updatedReactions[existingIndex].emoji = emoji; // update
      }
    } else {
      updatedReactions.push({
        user: currentUser._id,
        emoji,
      });
    }

    setReactions(updatedReactions);

    // update server with new reactions
    updateReactions(chat, updatedReactions);
  };

  return (
    <>
      {chat.isUpdated && (
        <small className="text-muted fst-italic mutedText">Edited</small>
      )}

      <div
        className="ctext-wrap position-relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={
            chatMessages[key]?.forwardedBy
              ? "ctext-wrap-content border-start border-2 border-primary"
              : "ctext-wrap-content"
          }
        >
          {/* ✅ Message Text */}
          {chat.message && (
            <div
              className={`d-inline-block p-2 rounded ${isSender ? "bg-primary text-white" : "bg-light text-dark"
                }`}
            >
              <p className="mb-0">{chat.message}</p>
            </div>
          )}

          {/* ✅ Media */}
          {chat.media && chat.media.length > 0 && (
            <>
              {isImage ? (
                <ChatMedia media={chat.media} />
              ) : isAudio ? (
                <div>
                  <audio controls>
                    <source
                      src={`${process.env.REACT_APP_API_URL}/${chat?.media[0]?.filePath}`}
                      type="audio/webm"
                    />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              ) : (
                isOtherFile && (
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted">
                      {chat?.media[0]?.fileName}
                    </span>
                    <a
                      className="px-2"
                      href={`${process.env.REACT_APP_API_URL}/${chat?.media[0]?.filePath}`}
                      download
                    >
                      <i className="ri-download-cloud-2-line fs-5"></i>
                    </a>
                  </div>
                )
              )}
            </>
          )}

          {/* ✅ File Message */}
          {chat.fileMessage && (
            <FileList fileName={chat.fileMessage} fileSize={chat.size} />
          )}
        </div>

        {showPicker && (
          <div
            className="position-absolute left-0 ml-5"
            style={{
              bottom: "100%",
              marginBottom: "8px",
              zIndex: 10,
            }}
          >
            <ReactionPicker onSelect={handleReaction} />
          </div>
        )}

        {reactions.length > 0 && (
          <div
            // className="position-absolute "
            className={`position-absolute ${isSender ? "start-0" : "end-0"}`}
            style={{
              bottom: "-10px",
              transform: "translateX(30%)", // push slightly outside bubble
              zIndex: 5,
            }}
          >
            <div
              className="d-flex align-items-center shadow-sm"
              style={{
                background: "#fff",
                borderRadius: "999px",
                border: "1px solid #e9ecef",
                padding: "2px 6px",
                gap: "2px",
              }}
            >
              {reactions.map((r, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: "12px",
                    lineHeight: 1,
                  }}
                >
                  {r.emoji}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ✅ Actions Dropdown */}
        {!chat.isTyping && (
          <ChatActionsDropdown
            chat={chat}
            message={chat.message}
            handleForwardCLick={handleForwardCLick}
            chatMessages={chatMessages}
            index={key}
            deleteMessage={deleteMessage}
            isGroup={isGroup}
            currentUser={currentUser}
            editHandler={editHandler}
          />
        )}
      </div>

      <ChatTime updatedAt={chat.updatedAt} formatDate={formatDate} />
    </>
  );
};

export default ChatContent;